import Docxtemplater from 'docxtemplater'
import ImageModule, { docxtemplater_image_module_namespace } from 'docxtemplater-image-module'
import TableModule from 'docxtemplater-table-module'
import expressionParser from 'docxtemplater/expressions'
import saveAs from 'file-saver'
import sizeOf from 'image-size'
import moment from 'moment'
import PizZip from 'pizzip'
import PizZipUtils from 'pizzip/utils/index.js'
import templateEN from '../../../../assets/documents/Netum_CV_EN.docx'
import templateFI from '../../../../assets/documents/Netum_CV_FIN.docx'
import pptxTemplate from '../../../../assets/documents/Netum_CV_PPTX.pptx'
import pptxTemplateEN from '../../../../assets/documents/Netum_CV_PPTX_EN.pptx'
import { localizedLanguageLevelForExport, localizedLanguageNameForExport } from '../../../../common/languageHelper'
import { IPersonCVInfo } from '../../../../interfaces/persons/IPerson'
import { IBundledCVProject, ICVProject, ICVProjectRole, ICVProjectSkill } from '../../../../interfaces/projects/IProjects'
import { CVSettings } from '../components/CVSettingsContext'

export function loadFile(url: string, callback: (err: Error, data: string) => void) {
  PizZipUtils.getBinaryContent(url, callback)
}

/**
 * Checks if a value is null, undefined, or an empty string
 */
export const isEmpty = (value: unknown): boolean => {
  if (value === null || value === undefined) {
    return true
  }

  if (typeof value === 'string') {
    return value.trim() === ''
  }

  return false
}

/**
 * Parses base64 image data for docxtemplater image module
 */
const base64Regex = /^(?:data:)?image\/(png|jpg|jpeg|svg|svg\+xml);base64,/

const validBase64 = /^(?:[A-Za-z0-9+/]{4})*(?:[A-Za-z0-9+/]{2}==|[A-Za-z0-9+/]{3}=)?$/

function base64Parser(tagValue: string): any {
  if (typeof tagValue !== 'string' || !base64Regex.test(tagValue)) {
    return false
  }

  const stringBase64 = tagValue.replace(base64Regex, '')

  if (!validBase64.test(stringBase64)) {
    throw new Error('Error parsing base64 data, your data contains invalid characters')
  }

  // For browsers, return a string (of binary content) :
  const binaryString = window.atob(stringBase64)
  const len = binaryString.length
  const bytes = new Uint8Array(len)
  for (let i = 0; i < len; i++) {
    const ascii = binaryString.charCodeAt(i)
    bytes[i] = ascii
  }
  return bytes.buffer
}
/**
 * Gets the skill level text based on the level number and language
 */
const getSkillLevelText = (skillLevel: number, language: string): string => {
  if (language === 'en') {
    switch (skillLevel) {
      case 5:
        return 'Expert'
      case 4:
        return 'Proficient Performer'
      case 3:
        return 'Competent Performer'
      case 2:
        return 'Advanced Beginner'
      case 1:
        return 'Novice'
      default:
        return 'Not Defined'
    }
  } else {
    switch (skillLevel) {
      case 5:
        return 'Ekspertti'
      case 4:
        return 'Taitava suorittaja'
      case 3:
        return 'Itsenäinen tekijä'
      case 2:
        return 'Kehittynyt aloittelija'
      case 1:
        return 'Noviisi'
      default:
        return 'Ei määritelty'
    }
  }
}

/**
 * Formats a month count as years and months, e.g. "1 v 2 kk" or "1 year 2 months"
 */
const formatYearsAndMonths = (totalMonths: number, language: string): string => {
  const rounded = Math.round(totalMonths)
  const years = Math.floor(rounded / 12)
  const months = rounded % 12
  const parts = []
  if (years > 0) parts.push(language === 'en' ? `${years} ${years === 1 ? 'year' : 'years'}` : `${years} v`)
  if (months > 0 || years === 0) parts.push(language === 'en' ? `${months} ${months === 1 ? 'month' : 'months'}` : `${months} kk`)
  return parts.join(' ')
}

/**
 * Formats experience duration based on the work_experience setting and language
 */
const formatExperienceDuration = (months: number, workExperienceFormat: string, language: string): string => {
  if (months <= 0) {
    return language === 'en' ? 'no experience' : 'ei kokemusta'
  }

  switch (workExperienceFormat) {
    case 'htkk':
      return `${months} ${language === 'en' ? 'person-months' : 'htkk'}`
    case 'htp':
      return `${Math.round(months * 21)} ${language === 'en' ? 'person-days' : 'htp'}`
    case 'year':
      return formatYearsAndMonths(months, language)
    default:
      return `${months} ${language === 'en' ? 'months' : 'kk'}`
  }
}

/**
 * Bundles skills by parent relationships, grouping child skills under parent skills
 */
const bundleSkillsByParent = (projectSkills: ICVProjectSkill[]): ICVProjectSkill[] => {
  if (!projectSkills || projectSkills.length === 0) return []

  // Group skills by parent relationships
  const parentGroups = new Map<string, ICVProjectSkill[]>()
  const standaloneSkills: ICVProjectSkill[] = []

  projectSkills.forEach((skill) => {
    const parentNameFi = skill.parentSkillNameFi
    const parentNameEng = skill.parentSkillNameEng

    if (parentNameFi || parentNameEng) {
      // This is a child skill - group it under its parent
      const parentKey = parentNameFi || parentNameEng || ''

      if (!parentGroups.has(parentKey)) {
        parentGroups.set(parentKey, [])
      }
      parentGroups.get(parentKey)?.push(skill)
    } else {
      // This is either a parent skill or a standalone skill
      standaloneSkills.push(skill)
    }
  })

  // Build the final array: standalone skills first, then grouped parent skills
  const bundledSkills: ICVProjectSkill[] = [...standaloneSkills]

  // Add grouped skills - represent each group with the first skill in the group
  // and combine experience/duration from all children
  parentGroups.forEach((childSkills, parentName) => {
    if (childSkills.length > 0) {
      const representativeSkill = { ...childSkills[0] }

      // Use parent name for display
      representativeSkill.skillNameFi = parentName
      representativeSkill.skillNameEng = parentName

      // Sum up experience from all child skills
      const totalKuukaudet = childSkills.reduce((sum, skill) => sum + skill.kuukaudet, 0)
      representativeSkill.kuukaudet = totalKuukaudet

      bundledSkills.push(representativeSkill)
    }
  })

  return bundledSkills
}

/**
 * Bundles projects by parent relationships, grouping child projects under parent projects
 * Calculates date ranges and prepares for skills/roles aggregation
 */
const bundleProjectsByParent = (projects: ICVProject[]): IBundledCVProject[] => {
  if (!projects || projects.length === 0) return []

  // Separate projects into those with parents and those without
  const projectsByParentId = new Map<number, ICVProject[]>()
  const projectsByParentName = new Map<string, ICVProject[]>()
  const standaloneProjects: ICVProject[] = []

  // First, identify all parent projects and group children
  projects.forEach((project) => {
    const hasParent = project.parentProjectId || project.parentProjectName

    if (hasParent) {
      // This project has a parent - group it
      if (project.parentProjectId) {
        if (!projectsByParentId.has(project.parentProjectId)) {
          projectsByParentId.set(project.parentProjectId, [])
        }
        projectsByParentId.get(project.parentProjectId)?.push(project)
      } else if (project.parentProjectName) {
        if (!projectsByParentName.has(project.parentProjectName)) {
          projectsByParentName.set(project.parentProjectName, [])
        }
        projectsByParentName.get(project.parentProjectName)?.push(project)
      }
    } else {
      // Standalone project (no parent)
      standaloneProjects.push(project)
    }
  })

  const bundledProjects: IBundledCVProject[] = []

  // Process standalone projects - check if any are parents
  standaloneProjects.forEach((project) => {
    const childrenById = projectsByParentId.get(project.projectId) || []
    const childrenByName = projectsByParentName.get(project.projectName) || []
    const allChildren = [...childrenById, ...childrenByName]

    if (allChildren.length > 0) {
      // This standalone project is a parent - bundle it with its children
      const allProjectsInGroup = [project, ...allChildren]

      // Calculate date range across all projects in group
      const startDates = allProjectsInGroup.map((p) => p.startDate).filter((d): d is Date => d !== null && d !== undefined)
      const endDates = allProjectsInGroup.map((p) => p.endDate).filter((d): d is Date => d !== null && d !== undefined)

      const earliestStart = startDates.length > 0 ? new Date(Math.min(...startDates.map((d) => new Date(d).getTime()))) : project.startDate
      const latestEnd = endDates.length > 0 ? new Date(Math.max(...endDates.map((d) => new Date(d).getTime()))) : project.endDate

      // Create bundled project using parent as base
      const bundledProject: IBundledCVProject = {
        ...project,
        startDate: earliestStart,
        endDate: latestEnd,
        // Store child project IDs for later skills/roles aggregation
        childProjectIds: allChildren.map((p) => p.projectId),
      }

      bundledProjects.push(bundledProject)

      // Remove these children from the maps so they're not processed again
      if (project.projectId) {
        projectsByParentId.delete(project.projectId)
      }
      projectsByParentName.delete(project.projectName)
    } else {
      // No children - just add as-is
      bundledProjects.push(project)
    }
  })

  // Process remaining orphaned children (parent not in the list)
  // Group them by parent and create synthetic parent projects
  projectsByParentId.forEach((children) => {
    if (children.length > 0) {
      const representative: IBundledCVProject = { ...children[0] }

      // Calculate date range across all children
      const startDates = children.map((p) => p.startDate).filter((d): d is Date => d !== null && d !== undefined)
      const endDates = children.map((p) => p.endDate).filter((d): d is Date => d !== null && d !== undefined)

      representative.startDate =
        startDates.length > 0 ? new Date(Math.min(...startDates.map((d) => new Date(d).getTime()))) : representative.startDate
      representative.endDate = endDates.length > 0 ? new Date(Math.max(...endDates.map((d) => new Date(d).getTime()))) : null

      // Use parent project name if available
      if (representative.parentProjectName) {
        representative.projectName = representative.parentProjectName
      }

      // Store child project IDs for later skills/roles aggregation
      representative.childProjectIds = children.map((p) => p.projectId)

      // Store the original parent's projectId to fetch its skills/roles
      // Even if parent was filtered out, we still want its skills/roles in the bundle
      if (children[0].parentProjectId) {
        representative.originalParentProjectId = children[0].parentProjectId
      }

      // Prioritize parent descriptions over child descriptions when available
      if (representative.parentProjectDescriptionFi) {
        representative.descriptionFi = representative.parentProjectDescriptionFi
      }
      if (representative.parentProjectDescriptionEng) {
        representative.descriptionEng = representative.parentProjectDescriptionEng
      }
      if (representative.parentProjectPersonDescriptionFi) {
        representative.personDescriptionFi = representative.parentProjectPersonDescriptionFi
      }
      if (representative.parentProjectPersonDescriptionEng) {
        representative.personDescriptionEng = representative.parentProjectPersonDescriptionEng
      }

      bundledProjects.push(representative)
    }
  })

  projectsByParentName.forEach((children, parentName) => {
    if (children.length > 0) {
      const representative: IBundledCVProject = { ...children[0] }

      // Calculate date range across all children
      const startDates = children.map((p) => p.startDate).filter((d): d is Date => d !== null && d !== undefined)
      const endDates = children.map((p) => p.endDate).filter((d): d is Date => d !== null && d !== undefined)

      representative.startDate =
        startDates.length > 0 ? new Date(Math.min(...startDates.map((d) => new Date(d).getTime()))) : representative.startDate
      representative.endDate = endDates.length > 0 ? new Date(Math.max(...endDates.map((d) => new Date(d).getTime()))) : null

      representative.projectName = parentName

      // Store child project IDs for later skills/roles aggregation
      representative.childProjectIds = children.map((p) => p.projectId)

      // Store the original parent's projectId to fetch its skills/roles
      // Even if parent was filtered out, we still want its skills/roles in the bundle
      if (children[0].parentProjectId) {
        representative.originalParentProjectId = children[0].parentProjectId
      }

      // Prioritize parent descriptions over child descriptions when available
      if (representative.parentProjectDescriptionFi) {
        representative.descriptionFi = representative.parentProjectDescriptionFi
      }
      if (representative.parentProjectDescriptionEng) {
        representative.descriptionEng = representative.parentProjectDescriptionEng
      }
      if (representative.parentProjectPersonDescriptionFi) {
        representative.personDescriptionFi = representative.parentProjectPersonDescriptionFi
      }
      if (representative.parentProjectPersonDescriptionEng) {
        representative.personDescriptionEng = representative.parentProjectPersonDescriptionEng
      }

      bundledProjects.push(representative)
    }
  })

  return bundledProjects
}

/**
 * Maps skills with experience information based on the settings
 */
const mapSkillsWithExperience = (
  skills: ICVProjectSkill[],
  settings: CVSettings,
  context: 'project' | 'workhistory' = 'project',
): string | null => {
  if (!skills || !Array.isArray(skills) || skills.length === 0) return null

  const { work_experience, inc_skillsexperience, inc_bundleskills, inc_skillsandroles } = settings

  let processedSkills = skills

  // Filter to key skills first if enabled
  if (inc_skillsandroles) {
    processedSkills = processedSkills.filter((skill) => skill.isKeySkill)
  }

  // Bundle skills by parent if enabled
  if (inc_bundleskills) {
    processedSkills = bundleSkillsByParent(processedSkills)
  }

  const prefix =
    context === 'workhistory'
      ? settings.cv_language === 'en'
        ? 'Skills used: '
        : 'Käytetyt taidot: '
      : settings.cv_language === 'en'
      ? "Specialist's skills: "
      : 'Asiantuntijan käyttämät taidot: '

  return (
    prefix +
    processedSkills
      .map((skill) => {
        if (inc_skillsexperience) {
          return `${
            settings.cv_language === 'en' && skill.skillNameEng?.length ? skill.skillNameEng : skill.skillNameFi
          } ${formatExperienceDuration(skill.kuukaudet, work_experience, settings.cv_language)}`
        }
        return settings.cv_language === 'en' && skill.skillNameEng?.length ? skill.skillNameEng : skill.skillNameFi
      })
      .join(', ')
  )
}

const mapRolesWithExperience = (
  roles: ICVProjectRole[],
  settings: CVSettings,
  context: 'project' | 'workhistory' = 'project',
): string | null => {
  if (!roles || !Array.isArray(roles) || roles.length === 0) return null
  const lng = settings.cv_language
  const prefix =
    context === 'workhistory'
      ? settings.cv_language === 'en'
        ? 'Roles in position: '
        : 'Roolit tehtävässä: '
      : settings.cv_language === 'en'
      ? "Specialist's roles in project: "
      : 'Asiantuntijan roolit projektissa: '

  return (
    prefix +
    roles
      .map((role) => {
        if (lng === 'en') {
          return role.roleNameEng?.length ? role.roleNameEng : role.roleNameFi || role.roleName || ''
        }
        return role.roleNameFi?.length ? role.roleNameFi : role.roleNameEng || role.roleName || ''
      })
      .filter((r) => r && r.trim().length)
      .join(', ')
  )
}

const mapWorkHistory = (PersonCVInfo: IPersonCVInfo, settings: CVSettings): any[] => {
  const {
    inc_workhistory_keyonly,
    inc_workhistory_jobtitle,
    inc_workhistory_timeframe,
    inc_workhistory_duration,
    inc_workhistory_industry,
    inc_workhistory_persondescription,
    inc_workhistory_personskills,
    inc_workhistory_personroles,
  } = settings

  return (
    PersonCVInfo?.projects
      ?.filter((project) => {
        // Only include work history projects (employer job titles projects)
        if (project.isEmployerJobTitlesProject !== true) return false

        // Filter by key employments if option is enabled
        if (inc_workhistory_keyonly && !project.isKeyProject) return false

        return true
      })
      ?.map((project) => {
        // Parse project name format: "company name - person name - job title"
        const projectNameParts = project.projectName.split(' - ')
        const companyName = projectNameParts[0] || ''
        const jobTitle = projectNameParts[2] || project.projectName

        // Get localized descriptions based on CV language setting
        const personDescription = settings.cv_language === 'en' ? project.personDescriptionEng : project.personDescriptionFi

        const startDate = project.startDate ? moment(project.startDate).format('M/YYYY') : ''
        const endDate = project.endDate ? moment(project.endDate).format('M/YYYY') : settings.cv_language === 'en' ? 'ongoing' : 'jatkuu'
        const duration = Math.ceil(
          moment(new Date(project.endDate ?? new Date())).diff(moment(new Date(project.startDate)), 'months', true),
        )

        // Build work history first line (company, optional job title, optional timeframe)
        let workhistoryFirstLine = companyName
        if (inc_workhistory_jobtitle) {
          workhistoryFirstLine += `, ${jobTitle}`
        }
        if (inc_workhistory_timeframe) {
          workhistoryFirstLine += `, ${startDate} – ${endDate}`
        }

        // Build work history second line (duration, industry)
        const secondLineParts = []
        if (inc_workhistory_duration && duration) {
          secondLineParts.push(formatYearsAndMonths(duration, settings.cv_language))
        }
        if (inc_workhistory_industry && project.branchName) {
          secondLineParts.push(project.branchName)
        }
        const workhistorySecondLine = secondLineParts.join(', ')

        // Get skills and roles for this work history entry
        const personSkills = PersonCVInfo?.projectSkills
        const personRoles = PersonCVInfo?.projectRoles
        const filteredSkills = personSkills.filter((skill) => skill.projectId === project.projectId)
        const filteredRoles = personRoles.filter((role) => role.projectId === project.projectId)

        return {
          workhistoryFirstLine,
          workhistorySecondLine,
          inc_workhistory_persondescription: inc_workhistory_persondescription && !isEmpty(personDescription),
          persondescription:
            inc_workhistory_persondescription && personDescription !== ''
              ? `${settings.cv_language === 'en' ? 'Job description: ' : 'Kuvaus työstä: '}${personDescription}`
              : null,
          inc_workhistory_personskills: inc_workhistory_personskills && filteredSkills.length > 0,
          inc_workhistory_personroles: inc_workhistory_personroles && filteredRoles.length > 0,
          personskills: inc_workhistory_personskills ? mapSkillsWithExperience(filteredSkills, settings, 'workhistory') : null,
          personroles: inc_workhistory_personroles ? mapRolesWithExperience(filteredRoles, settings, 'workhistory') : null,
        }
      }) || []
  )
}

const mapProjects = (PersonCVInfo: IPersonCVInfo, settings: CVSettings): any[] => {
  const {
    inc_netumonly,
    inc_keyprojects,
    inc_bundlesubprojects,
    inc_projectclientname,
    inc_projectemployer,
    inc_projectduration,
    inc_projecttimeframe,
    inc_employer,
    inc_projectindustry,
    inc_projectdescription,
    inc_persondescription,
    inc_personskills,
    inc_personroles,
  } = settings

  // First, filter projects
  const filteredProjects: ICVProject[] =
    PersonCVInfo?.projects?.filter((project) => {
      // Filter out work history projects (employer job titles projects)
      if (project.isEmployerJobTitlesProject === true) return false

      // Filter out non-Netum projects if Netum-only is enabled
      if (inc_netumonly && project.employerId !== 0) return false

      // Filter by key projects if option is enabled
      if (inc_keyprojects && !project.isKeyProject) return false

      return true
    }) || []

  // Bundle projects by parent if enabled
  const bundledProjects: IBundledCVProject[] = inc_bundlesubprojects ? bundleProjectsByParent(filteredProjects) : filteredProjects

  return (
    bundledProjects?.map((project) => {
      // Get localized descriptions based on CV language setting
      // Prioritize parent project descriptions over child project descriptions
      const projectDescription =
        settings.cv_language === 'en'
          ? project.parentProjectDescriptionEng || project.descriptionEng
          : project.parentProjectDescriptionFi || project.descriptionFi

      const personDescription =
        settings.cv_language === 'en'
          ? project.parentProjectPersonDescriptionEng || project.personDescriptionEng
          : project.parentProjectPersonDescriptionFi || project.personDescriptionFi

      const startDate = project.startDate ? moment(project.startDate).format('D.M.YYYY') : ''
      const endDate = project.endDate ? moment(project.endDate).format('D.M.YYYY') : ''
      const duration = Math.ceil(moment(new Date(project.endDate ?? new Date())).diff(moment(new Date(project.startDate)), 'months', true))

      // Build project first line (name, client, employer)
      let projectFirstLine = project.projectName
      if (inc_projectclientname && project.customer) {
        projectFirstLine += `, ${project.customer}`
      }
      if (inc_projectemployer && project.employerName) {
        projectFirstLine += `, ${project.employerName}`
      }

      // Build project second line (duration, timeframe, employer, industry)
      const secondLineParts = []
      if (inc_projectduration && duration) {
        // Project duration is calendar time, so only the years format applies. Person-days and person-months are effort units.
        secondLineParts.push(
          settings.work_experience === 'year'
            ? formatYearsAndMonths(duration, settings.cv_language)
            : `${duration} ${settings.cv_language === 'en' ? 'months' : 'kk'}`,
        )
      }
      if (inc_projecttimeframe) {
        secondLineParts.push(`${startDate} - ${endDate || (settings.cv_language === 'en' ? 'ongoing' : 'jatkuu')}`)
      }
      if (inc_employer && project.employerName) {
        secondLineParts.push(`${settings.cv_language === 'en' ? 'employer' : 'työnantaja'} ${project.employerName}`)
      }
      if (inc_projectindustry && project.branchName) {
        secondLineParts.push(project.branchName)
      }
      const projectSecondLine = secondLineParts.join(', ')

      // Get skills and roles for this project
      const personSkills = PersonCVInfo?.projectSkills
      const personRoles = PersonCVInfo?.projectRoles

      // For bundled projects, include skills/roles from all child projects
      // Also include parent's original projectId if it was filtered out (orphaned children scenario)
      const projectIds = project.childProjectIds
        ? [project.projectId, ...project.childProjectIds, ...(project.originalParentProjectId ? [project.originalParentProjectId] : [])]
        : [project.projectId]

      let filteredSkills = personSkills.filter((skill) => projectIds.includes(skill.projectId))
      let filteredRoles = personRoles.filter((role) => projectIds.includes(role.projectId))

      // If bundled, merge and sum experience for duplicate skills/roles
      if (project.childProjectIds) {
        // Merge skills by skill name and sum experience
        const skillsMap = new Map<string, ICVProjectSkill>()
        filteredSkills.forEach((skill) => {
          const key = skill.skillNameFi || skill.skillNameEng || ''
          if (skillsMap.has(key)) {
            const existing = skillsMap.get(key)!
            existing.kuukaudet += skill.kuukaudet
            // Preserve isKeySkill if either instance is marked as key
            if (skill.isKeySkill && !existing.isKeySkill) {
              existing.isKeySkill = true
            }
          } else {
            skillsMap.set(key, { ...skill })
          }
        })
        filteredSkills = Array.from(skillsMap.values())

        // Merge roles by role name (no experience summing for roles in projectRoles)
        const rolesMap = new Map<string, ICVProjectRole>()
        filteredRoles.forEach((role) => {
          const key = role.roleNameFi || role.roleNameEng || ''
          if (!rolesMap.has(key)) {
            rolesMap.set(key, { ...role })
          }
        })
        filteredRoles = Array.from(rolesMap.values())
      }

      return {
        projectFirstLine,
        projectSecondLine,
        inc_projectdescription: inc_projectdescription && !isEmpty(projectDescription),
        inc_persondescription: inc_persondescription && !isEmpty(personDescription),
        projectdescription: inc_projectdescription
          ? `${settings.cv_language === 'en' ? 'Project description: ' : 'Projektin kuvaus: '}${projectDescription}`
          : null,
        persondescription:
          inc_persondescription && personDescription !== ''
            ? `${
                settings.cv_language === 'en' ? "Specialist's description of their work: " : 'Asiantuntijan kuvaus työstään: '
              }${personDescription}`
            : null,
        inc_personskills: inc_personskills && filteredSkills.length > 0,
        inc_personroles: inc_personroles && filteredRoles.length > 0,
        personskills: inc_personskills ? mapSkillsWithExperience(filteredSkills, settings) : null,
        personroles: inc_personroles ? mapRolesWithExperience(filteredRoles, settings) : null,
      }
    }) || []
  )
}

const mapSkills = (PersonCVInfo: IPersonCVInfo, settings: CVSettings): any[] => {
  const { cv_language, work_experience, inc_keyskills } = settings
  const lang = cv_language || 'fi'

  let skills = PersonCVInfo?.skills || []

  // Filter by key skills if option is enabled
  if (inc_keyskills) {
    skills = skills.filter((skill) => skill.isKeySkill === true)
  }

  return (
    skills.map((skill) => ({
      name: lang === 'en' && skill.skillNameEng ? skill.skillNameEng : skill.skillName,
      level: getSkillLevelText(skill.skillLevel, lang),
      months: formatExperienceDuration(skill.expMonths, work_experience, lang),
      last_used: skill.lastUsed ? skill.lastUsed : lang === 'en' ? 'in use' : 'käytössä',
    })) || []
  )
}

const mapSkillsTable = (PersonCVInfo: IPersonCVInfo, settings: CVSettings): any => {
  const {
    cv_language,
    work_experience,
    inc_keyskills,
    inc_skillscategories,
    inc_skillslevel,
    inc_skillskillsexperience,
    inc_skillslastused,
    inc_skillsdescriptions,
  } = settings
  const lang = cv_language || 'fi'

  let skills = PersonCVInfo?.skills || []

  // Filter by key skills if option is enabled
  if (inc_keyskills) {
    skills = skills.filter((skill) => skill.isKeySkill === true)
  }

  // Build dynamic headers based on settings
  const headerConfig = [
    { condition: true, fi: 'Taito', en: 'Skill' },
    { condition: inc_skillslevel, fi: 'Taso', en: 'Level' },
    { condition: inc_skillskillsexperience, fi: 'Kokemus', en: 'Experience' },
    { condition: inc_skillslastused, fi: 'Viimeksi käytetty', en: 'Last Used' },
    { condition: inc_skillsdescriptions, fi: 'Kuvaus', en: 'Description' },
  ]
  const headers = headerConfig.filter((h) => h.condition).map((h) => (lang === 'en' ? h.en : h.fi))

  // Helper function to build skill row
  const buildSkillRow = (skill: any) => {
    const row = [lang === 'en' && skill.skillNameEng ? skill.skillNameEng : skill.skillName]
    if (inc_skillslevel) row.push(getSkillLevelText(skill.skillLevel, lang))
    if (inc_skillskillsexperience) row.push(formatExperienceDuration(skill.expMonths, work_experience, lang))
    if (inc_skillslastused) row.push(skill.lastUsed || (lang === 'en' ? 'in use' : 'käytössä'))
    if (inc_skillsdescriptions) row.push('') // TODO: Add skill description
    return row
  }

  // Helper function to create table config
  const createTableConfig = (data: any[]) => ({
    header: headers,
    data,
    widths: Array(headers.length)
      .fill(0)
      .map((_, i) => (i === 0 ? 150 : i === headers.length - 1 ? 200 : 100)),
    style: { header: { textAlign: 'left' }, normal: { textAlign: 'left' } },
    border: { type: 'all', size: 0, color: '000000' },
    pageBreakBetween: false,
    finalPageBreak: false,
  })

  // Category mode: group skills and add category headers
  if (inc_skillscategories) {
    const skillsByCategory = skills.reduce((map, skill) => {
      const categoryName = lang === 'en' && skill.skillCategoryNameEng ? skill.skillCategoryNameEng : skill.skillCategoryName
      if (!map.has(categoryName)) map.set(categoryName, [])
      map.get(categoryName)?.push(skill)
      return map
    }, new Map<string, typeof skills>())

    const allData: any[] = []
    skillsByCategory.forEach((categorySkills, categoryName) => {
      // Add category header row
      const categoryRow = [
        { text: categoryName, style: { runProperties: '<w:b/>', textAlign: 'left' } },
        ...Array(headers.length - 1).fill({ text: '' }),
      ]
      allData.push(categoryRow)
      // Add skill rows
      categorySkills.forEach((skill) => allData.push(buildSkillRow(skill)))
    })

    return { skillsTable: createTableConfig(allData) }
  }

  // Single table mode
  return { skillsTable: createTableConfig(skills.map(buildSkillRow)) }
}

interface KnowhowItem {
  name: string
  experience: number
}

const getLocalizedRoleName = (role: any, lang: string): string => {
  return lang === 'en' && role.roleNameEng ? role.roleNameEng : role.roleNameFi
}

const mapKnowhow = (PersonCVInfo: IPersonCVInfo, settings: CVSettings, inc_showskills = true): { name: string }[] => {
  const { showExperienceDuration, work_experience, cv_language, inc_keyexpertise, inc_showroles, inc_keyroles } = settings
  const lang = cv_language || 'fi'

  // Build combined items array with both skills and roles
  const skillItems: KnowhowItem[] = inc_showskills
    ? (PersonCVInfo?.skills || [])
        .filter((skill) => !inc_keyexpertise || skill.isKeySkill)
        .map((skill) => ({
          name: lang === 'en' && skill.skillNameEng ? skill.skillNameEng : skill.skillName,
          experience: skill.expMonths || 0,
        }))
    : []

  const roleItems: KnowhowItem[] = inc_showroles
    ? (PersonCVInfo?.roles || [])
        .filter((role) => !inc_keyroles || role.isKeyRole)
        .map((role) => ({
          name: getLocalizedRoleName(role, lang),
          experience: role.experience || 0,
        }))
    : []

  const combinedItems = [...skillItems, ...roleItems]

  // Sort and format items based on experience display setting
  const sortedNames = showExperienceDuration
    ? combinedItems
        .sort((a, b) => b.experience - a.experience)
        .map((item) => `${item.name} ${formatExperienceDuration(item.experience, work_experience, lang)}`)
    : combinedItems.map((item) => item.name).sort((a, b) => a.localeCompare(b))
  return sortedNames.map((name) => ({ name }))
}

const mapKnowhowTable = (PersonCVInfo: IPersonCVInfo, settings: CVSettings, inc_showskills = true): any => {
  const { showExperienceDuration, work_experience, cv_language, inc_keyexpertise, inc_showroles, inc_keyroles } = settings
  const lang = cv_language || 'fi'

  // Build combined items array with both skills and roles
  const skillItems: KnowhowItem[] = inc_showskills
    ? (PersonCVInfo?.skills || [])
        .filter((skill) => !inc_keyexpertise || skill.isKeySkill)
        .map((skill) => ({
          name: lang === 'en' && skill.skillNameEng ? skill.skillNameEng : skill.skillName,
          experience: skill.expMonths || 0,
        }))
    : []

  const roleItems: KnowhowItem[] = inc_showroles
    ? (PersonCVInfo?.roles || [])
        .filter((role) => !inc_keyroles || role.isKeyRole)
        .map((role) => ({
          name: getLocalizedRoleName(role, lang),
          experience: role.experience || 0,
        }))
    : []

  const combinedItems = [...skillItems, ...roleItems]

  // Sort and format items based on experience display setting
  const sortedNames = showExperienceDuration
    ? combinedItems
        .sort((a, b) => b.experience - a.experience)
        .map((item) => `${item.name} ${formatExperienceDuration(item.experience, work_experience, lang)}`)
    : combinedItems.map((item) => item.name).sort((a, b) => a.localeCompare(b))

  // Build table data with two columns, bullets, and bold formatting
  const tableData: any[] = []

  for (let i = 0; i < sortedNames.length; i += 2) {
    const leftItem = sortedNames[i]
    const rightItem = sortedNames[i + 1] || ''

    tableData.push([
      { text: `• ${leftItem}`, style: { runProperties: '<w:b/>', textAlign: 'left' } },
      { text: rightItem ? `• ${rightItem}` : '', style: { runProperties: '<w:b/>', textAlign: 'left' } },
    ])
  }

  // Create table config (minimal headers for TableModule requirement, borderless)
  const tableConfig = {
    header: ['', ''], // Empty headers required by TableModule (will be invisible)
    data: tableData,
    widths: [250, 250],
    style: { header: { textAlign: 'left' }, normal: { textAlign: 'left' } },
    border: { type: 'all', size: 0, color: '000000' },
    pageBreakBetween: false,
    finalPageBreak: false,
  }

  return { knowhowTable: tableConfig }
}

const mapLanguages = (PersonCVInfo: IPersonCVInfo, settings: CVSettings): any[] => {
  const { inc_languagelevel, inc_languagedescription, cv_language } = settings

  return (
    PersonCVInfo?.languages?.map((language) => {
      let text = localizedLanguageNameForExport(language.languageName.trim(), cv_language)

      if (language.languageLevel && inc_languagelevel) {
        text += `, ${localizedLanguageLevelForExport(language.languageLevel, cv_language)}`
      }

      if (inc_languagedescription && !isEmpty(language.description)) {
        text += `, ${language.description}`
      }

      return {
        text,
      }
    }) || []
  )
}

const mapEducations = (PersonCVInfo: IPersonCVInfo, settings: CVSettings): any[] => {
  const { educationOption, inc_eduschool, inc_edufinish, inc_eduinfo } = settings

  // Filter and sort educations
  const filteredEducations = PersonCVInfo?.educations
    ?.filter((education) => {
      if (educationOption === 'highest') {
        return education.isHighestDegree === true
      }
      if (educationOption === 'key') {
        return education.isKeyEducation === true
      }
      return true
    })
    .sort((a, b) => {
      // First sort by isHighestDegree (true values first)
      if (a.isHighestDegree && !b.isHighestDegree) return -1
      if (!a.isHighestDegree && b.isHighestDegree) return 1

      // Then sort by endDate (newest first)
      const dateA = new Date(a.endDate || 0)
      const dateB = new Date(b.endDate || 0)
      return dateB.getTime() - dateA.getTime()
    })

  return (
    filteredEducations.map((education) => {
      if (education.degree) {
        let text = education.degree
        if (inc_eduschool) {
          text += `, ${education.school}`
        }
        if (inc_edufinish) {
          text += `, ${moment(education.endDate).format('YYYY')}`
        }
        return {
          text,
          info: education.comment,
          inc_eduinfo: inc_eduinfo && !isEmpty(education.comment),
        }
      }
    }) || []
  )
}

const mapCertificates = (PersonCVInfo: IPersonCVInfo, settings: CVSettings): any[] => {
  const { cv_language, inc_certissuer, inc_certvalidity, inc_certdetails, inc_certshowexpired } = settings

  return (
    PersonCVInfo?.certificates
      ?.filter((cert) => {
        // Only include non-expired certificates unless inc_certshowexpired is true
        if (inc_certshowexpired) return true
        if (!cert.expirationDate) return true // No expiration date means always valid
        return new Date(cert.expirationDate) >= new Date()
      })
      .map((cert) => {
        let text = cert.name

        // Add issuer information if enabled
        if (inc_certissuer && cert.school) {
          text += `, ${cert.school}`
        }

        // Add validity information if enabled
        if (inc_certvalidity && cert.expirationDate) {
          text += `, ${cv_language === 'fi' ? 'voimassa' : 'valid'} ${moment(cert.expirationDate).format('DD.MM.YYYY')}`
        }

        return {
          text,
          inc_certdetails: inc_certdetails && !isEmpty(cert.comment),
          details: cert.comment,
        }
      }) || []
  )
}

const prepareDocumentData = (PersonCVInfo: IPersonCVInfo, settings: CVSettings) => {
  const {
    inc_title,
    inc_email,
    inc_picture,
    inc_summary,
    inc_workexperiencedescription,
    inc_education,
    inc_certificates,
    inc_workhistory,
    inc_projects,
    inc_languages,
    inc_skillsleveldescriptions,
    inc_skills,
    inc_knowhow,
  } = settings

  // Map all required data
  const mappedWorkHistory = mapWorkHistory(PersonCVInfo, settings)
  const mappedProjects = mapProjects(PersonCVInfo, settings)
  const mappedSkills = mapSkills(PersonCVInfo, settings)
  const mappedSkillsTable = mapSkillsTable(PersonCVInfo, settings)
  const mappedLanguages = mapLanguages(PersonCVInfo, settings)
  const mappedEducations = mapEducations(PersonCVInfo, settings)
  const mappedCertificates = mapCertificates(PersonCVInfo, settings)
  const mappedKnowhowTable = mapKnowhowTable(PersonCVInfo, settings)

  const profileImageData =
    inc_picture && PersonCVInfo?.personDetails.largePhoto ? `data:image/jpeg;base64,${PersonCVInfo.personDetails.largePhoto}` : null

  // Return template data
  return {
    full_name: PersonCVInfo?.personDetails.name,
    inc_title:
      !isEmpty(settings.cv_language === 'en' ? PersonCVInfo?.personDetails.titleEng : PersonCVInfo?.personDetails.titleFi) && inc_title,
    title: settings.cv_language === 'en' ? PersonCVInfo?.personDetails.titleEng : PersonCVInfo?.personDetails.titleFi,
    inc_email,
    email: PersonCVInfo?.personDetails.username,
    inc_summary:
      !isEmpty(settings.cv_language === 'en' ? PersonCVInfo?.personDetails.summaryEng : PersonCVInfo?.personDetails.summaryFi) &&
      inc_summary,
    summary: settings.cv_language === 'en' ? PersonCVInfo?.personDetails.summaryEng : PersonCVInfo?.personDetails.summaryFi,
    inc_workexperiencedescription:
      !isEmpty(
        settings.cv_language === 'en'
          ? PersonCVInfo?.personDetails.workExperienceDescriptionEng
          : PersonCVInfo?.personDetails.workExperienceDescriptionFi,
      ) && inc_workexperiencedescription,
    workexperiencedescription:
      settings.cv_language === 'en'
        ? PersonCVInfo?.personDetails.workExperienceDescriptionEng
        : PersonCVInfo?.personDetails.workExperienceDescriptionFi,
    inc_picture,
    profile_picture: profileImageData,
    inc_educations: mappedEducations.length > 0 && inc_education,
    educations: mappedEducations,
    inc_certificates: mappedCertificates.length > 0 && inc_certificates,
    certificates: mappedCertificates,
    inc_workhistory: inc_workhistory && mappedWorkHistory && mappedWorkHistory.length > 0,
    workhistory: mappedWorkHistory,
    inc_projects: inc_projects && mappedProjects && mappedProjects.length > 0,
    projects: mappedProjects,
    inc_skills,
    skills: mappedSkills,
    ...mappedSkillsTable,
    inc_languages: inc_languages && mappedLanguages && mappedLanguages.length > 0,
    languages: mappedLanguages,
    inc_skillsleveldescriptions: inc_skills && inc_skillsleveldescriptions,
    inc_knowhow: inc_knowhow && mappedKnowhowTable.knowhowTable.data.length > 0,
    ...mappedKnowhowTable,
  }
}

const mapPPTXProjects = (PersonCVInfo: IPersonCVInfo, settings: CVSettings): string => {
  const { pptx_inc_keyprojects, pptx_inc_netumonly, pptx_inc_projectclientname, inc_bundlesubprojects } = settings

  const filteredProjects: ICVProject[] =
    PersonCVInfo?.projects?.filter((project) => {
      if (project.isEmployerJobTitlesProject === true) return false
      if (pptx_inc_netumonly && project.employerId !== 0) return false
      if (pptx_inc_keyprojects && !project.isKeyProject) return false
      return true
    }) || []

  const bundledProjects: IBundledCVProject[] = inc_bundlesubprojects ? bundleProjectsByParent(filteredProjects) : filteredProjects

  const projects =
    bundledProjects
      ?.map((project) => {
        if (!project.projectName) return null
        let projectText = project.projectName
        if (pptx_inc_projectclientname && project.customer) {
          projectText += ` - ${project.customer}`
        }
        return projectText
      })
      .filter((text): text is string => text !== null && text.length > 0) || []

  return projects.join(', ')
}

const mapPPTXWorkHistory = (PersonCVInfo: IPersonCVInfo, settings: CVSettings): string[] => {
  const { pptx_inc_workhistory_keyonly, pptx_inc_workhistory_jobtitle, pptx_inc_workhistory_timeframe } = settings

  const result =
    PersonCVInfo?.projects
      ?.filter((project) => {
        // Only include work history projects (employer job titles projects)
        if (project.isEmployerJobTitlesProject !== true) return false

        // Filter by key employments if option is enabled
        if (pptx_inc_workhistory_keyonly && !project.isKeyProject) return false

        return true
      })
      ?.map((project) => {
        if (!project.projectName) return null

        const projectNameParts = project.projectName.split(' - ')
        const companyName = projectNameParts[0] || ''
        const jobTitle = projectNameParts[2] || project.projectName

        if (!companyName) return null

        let workText = companyName
        if (pptx_inc_workhistory_jobtitle && jobTitle) {
          workText += `, ${jobTitle}`
        }
        if (pptx_inc_workhistory_timeframe) {
          const startDate = project.startDate ? moment(project.startDate).format('M/YYYY') : ''
          const endDate = project.endDate ? moment(project.endDate).format('M/YYYY') : settings.cv_language === 'en' ? 'ongoing' : 'jatkuu'
          if (startDate || endDate) {
            workText += `, ${startDate} – ${endDate}`
          }
        }
        return workText
      })
      .filter((text): text is string => text !== null && text.length > 0) || []

  return result
}

const mapPPTXEducations = (PersonCVInfo: IPersonCVInfo, settings: CVSettings): string[] => {
  const { pptx_inc_edu_highestonly, pptx_inc_edu_keyonly, pptx_inc_eduschool, pptx_inc_edufinish } = settings

  const filteredEducations = PersonCVInfo?.educations?.filter((education) => {
    if (pptx_inc_edu_highestonly && !education.isHighestDegree) return false
    if (pptx_inc_edu_keyonly && !education.isKeyEducation) return false
    return true
  })

  return (
    filteredEducations
      ?.map((education) => {
        if (!education.degree) return null
        let text = education.degree
        if (pptx_inc_eduschool && education.school) {
          text += `, ${education.school}`
        }
        if (pptx_inc_edufinish && education.endDate) {
          text += `, ${moment(education.endDate).format('YYYY')}`
        }
        return text
      })
      .filter((text): text is string => text !== null) || []
  )
}

const mapPPTXCertificates = (PersonCVInfo: IPersonCVInfo, settings: CVSettings): string[] => {
  const { pptx_inc_certissuer } = settings

  return (
    PersonCVInfo?.certificates
      ?.filter((cert) => !cert.expirationDate || new Date(cert.expirationDate) >= new Date())
      ?.map((cert) => {
        if (!cert.name) return null
        let text = cert.name
        if (pptx_inc_certissuer && cert.school) {
          text += `, ${cert.school}`
        }
        return text
      })
      .filter((text): text is string => text !== null) || []
  )
}

const preparePPTXDocumentData = (PersonCVInfo: IPersonCVInfo, settings: CVSettings) => {
  const {
    pptx_inc_title,
    pptx_inc_email,
    pptx_inc_picture,
    pptx_inc_summary,
    pptx_inc_workexperiencedescription,
    pptx_inc_knowhow,
    pptx_inc_showskills,
    pptx_inc_keyskills,
    pptx_inc_showroles,
    pptx_inc_keyroles,
    pptx_inc_experiencetime,
    pptx_inc_education,
    pptx_inc_certificates,
    pptx_inc_workhistory,
    pptx_inc_projects,
  } = settings

  // Map PowerPoint-specific data using simplified mappers - only if the card is enabled
  const mappedProjects = pptx_inc_projects ? mapPPTXProjects(PersonCVInfo, settings) : ''
  const mappedWorkHistory = pptx_inc_workhistory ? mapPPTXWorkHistory(PersonCVInfo, settings) : []
  const mappedEducations = pptx_inc_education ? mapPPTXEducations(PersonCVInfo, settings) : []
  const mappedCertificates = pptx_inc_certificates ? mapPPTXCertificates(PersonCVInfo, settings) : []

  // For skills and roles, create temporary settings to reuse existing mapKnowhowTable
  // mapKnowhowTable now handles both skills and roles in a single combined list
  const pptxSettings: CVSettings = {
    ...settings,
    inc_keyexpertise: pptx_inc_keyskills,
    inc_showroles: pptx_inc_showroles,
    inc_keyroles: pptx_inc_keyroles,
    showExperienceDuration: pptx_inc_experiencetime,
  }

  const mappedKnowhow = pptx_inc_showskills || pptx_inc_showroles ? mapKnowhow(PersonCVInfo, pptxSettings, pptx_inc_showskills) : []

  const profileImageData =
    pptx_inc_picture && PersonCVInfo?.personDetails.largePhoto ? `data:image/jpeg;base64,${PersonCVInfo.personDetails.largePhoto}` : null

  // Return PowerPoint template data - projects as comma-separated string, others as arrays
  const templateData = {
    full_name: PersonCVInfo?.personDetails.name,
    inc_title:
      !isEmpty(settings.cv_language === 'en' ? PersonCVInfo?.personDetails.titleEng : PersonCVInfo?.personDetails.titleFi) &&
      pptx_inc_title,
    title: pptx_inc_title
      ? settings.cv_language === 'en'
        ? PersonCVInfo?.personDetails.titleEng
        : PersonCVInfo?.personDetails.titleFi
      : '',
    inc_email: pptx_inc_email,
    email: pptx_inc_email ? PersonCVInfo?.personDetails.username : '',
    inc_summary:
      !isEmpty(settings.cv_language === 'en' ? PersonCVInfo?.personDetails.summaryEng : PersonCVInfo?.personDetails.summaryFi) &&
      pptx_inc_summary,
    summary: pptx_inc_summary
      ? settings.cv_language === 'en'
        ? PersonCVInfo?.personDetails.summaryEng
        : PersonCVInfo?.personDetails.summaryFi
      : '',
    inc_workexperiencedescription:
      !isEmpty(
        settings.cv_language === 'en'
          ? PersonCVInfo?.personDetails.workExperienceDescriptionEng
          : PersonCVInfo?.personDetails.workExperienceDescriptionFi,
      ) && pptx_inc_workexperiencedescription,
    workexperiencedescription: pptx_inc_workexperiencedescription
      ? settings.cv_language === 'en'
        ? PersonCVInfo?.personDetails.workExperienceDescriptionEng
        : PersonCVInfo?.personDetails.workExperienceDescriptionFi
      : '',
    inc_picture: pptx_inc_picture,
    profile_picture: profileImageData,
    inc_educations: pptx_inc_education && mappedEducations.length > 0,
    educations: pptx_inc_education ? mappedEducations.map((text) => ({ text })) : [],
    inc_certificates: pptx_inc_certificates && mappedCertificates.length > 0,
    certificates: pptx_inc_certificates ? mappedCertificates.map((text) => ({ text })) : [],
    inc_workhistory: pptx_inc_workhistory && mappedWorkHistory.length > 0,
    workhistory: pptx_inc_workhistory ? mappedWorkHistory.map((text) => ({ text })) : [],
    inc_projects: pptx_inc_projects && mappedProjects.length > 0,
    projects: pptx_inc_projects ? mappedProjects : '',
    inc_knowhow: pptx_inc_knowhow && mappedKnowhow.length > 0,
    knowhow: mappedKnowhow,
  }

  return templateData
}

export const generateDocument = (PersonCVInfo: IPersonCVInfo, settings: CVSettings) => {
  const isPowerPoint = settings.cv_base === 'pptx'
  const selectedTemplate = isPowerPoint
    ? settings.cv_language === 'en'
      ? pptxTemplateEN
      : pptxTemplate
    : settings.cv_language === 'en'
    ? templateEN
    : templateFI

  const imageOptions: docxtemplater_image_module_namespace.ImageOptions = {
    getImage(tagValue: string): ArrayBuffer | false {
      return base64Parser(tagValue)
    },
    getSize(imgData: string | Buffer, _data: any, _tagValue: string, options: any): [number, number] {
      const incPicture = isPowerPoint ? settings.pptx_inc_picture : settings.inc_picture
      if (incPicture === false) {
        return [0, 0]
      }

      const part = options?.part
      if (part?.module === 'open-xml-templating/docxtemplater-replace-image-module') {
        return [part.width || 150, part.height || 150]
      }

      let buffer: Buffer
      if (typeof imgData === 'string') {
        buffer = Buffer.from(imgData, 'binary')
      } else {
        buffer = imgData
      }

      const sizeObj = sizeOf(new Uint8Array(buffer))
      return [sizeObj.width || 150, sizeObj.height || 150]
    },
  }

  loadFile(selectedTemplate, function (error, content) {
    if (error) {
      console.error('Error loading template:', error)
      throw error
    }

    try {
      // Create document
      const zip = new PizZip(content)
      const doc = new Docxtemplater(zip, {
        paragraphLoop: true,
        linebreaks: true,
        parser: expressionParser,
        modules: [new ImageModule(imageOptions), new TableModule.Full()],
      })

      // Prepare data and render document
      const data = isPowerPoint ? preparePPTXDocumentData(PersonCVInfo, settings) : prepareDocumentData(PersonCVInfo, settings)
      doc.render(data)

      // Generate output file
      const mimeType = isPowerPoint
        ? 'application/vnd.openxmlformats-officedocument.presentationml.presentation'
        : 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'

      const out = doc.getZip().generate({
        type: 'blob',
        mimeType,
      })

      // Create filename and save
      const currentDate = moment().format('YYYYMMDD')
      const personName = PersonCVInfo?.personDetails.name || 'Unknown'
      const extension = isPowerPoint ? 'pptx' : 'docx'
      const filename = `${currentDate}_${personName}.${extension}`
      saveAs(out, filename)
    } catch (error) {
      console.error('Error generating document:', error)
      throw error
    }
  })
}
